// iter150AZ.112 — DR404 Web Serial configurator dialog.
//
// Run inside the Sequencing Group Builder (Step 2, Transparent TCP
// RTU rows). Drives the full state machine described in the user's
// spec:
//
//   1. Connect Serial (browser permission prompt)
//   2. Enter AT Mode (auto baud-rate sweep)
//   3. Enable AP+STA   → AT+FAPSTA=on
//   4. Activation/reset → AT+RELD (reboots)
//   5. Reconnect after reboot
//   6. Read MAC → AT+WSMAC
//   7. Generate register + heartbeat frames (CRC16 Modbus)
//   8. Apply DR404 configuration (the 14-command sequence)
//   9. AT+Z reboot + verify
//  10. Save generated frames + MAC into the wizard row
//
// Fail-safe: if any *required* AT command fails, the runner stops and
// surfaces the error with a Retry button (no need to dismiss the
// dialog). Reboots are only triggered after explicit operator
// confirmation.
//
// iter150AZ.113 — added:
//   * The dialog no longer closes when the user clicks the overlay or
//     presses Escape. They have to use Cancel explicitly, and if a
//     serial operation is in flight we ask them to confirm before
//     tearing it down.
//   * AbortController plumbed through every step; closing the dialog
//     mid-AT now actually unblocks pending reads instead of just
//     leaking the port lock.
//   * Retry button visible on the error card — no need to close and
//     re-open the dialog to recover from a flaky USB enumeration.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plug, ArrowsClockwise, CheckCircle, Warning, X, FloppyDisk } from "@phosphor-icons/react";
import {
  buildRegisterFrame,
  buildHeartbeatFrame,
  normalizeMac,
  apSsidFromMac,
  validateFrame,
  REGISTER_FN_CODE,
  HEARTBEAT_FN_CODE,
} from "@/lib/dr404Crc";
import {
  Dr404Serial,
  connectAndEnterAtMode,
  isWebSerialSupported,
  sleep,
} from "@/lib/dr404Serial";


// Command sequence that personalises a DR404 for our TCP RTU usage.
// ``AT+Z`` is appended at the end as a separate confirmation step so
// the operator can review the queued config before the reboot.
function buildAtSequence({ macSuffix4, registerHex, heartbeatHex,
                            serverPort, serverAddress }) {
  return [
    `AT+WMODE=STA`,
    `AT+TMODE=Through`,
    `AT+WAP=11BGN,ACC WiFi ${macSuffix4},AUTO`,
    `AT+UART=9600,8,1,None,NFC`,
    `AT+REGEN=usr`,
    `AT+REGTCP=first`,
    `AT+REGUSR=${registerHex}`,
    `AT+HEARTEN=on`,
    `AT+HEARTTP=NET`,
    `AT+HEARTDT=${heartbeatHex}`,
    `AT+HEARTTM=30`,
    `AT+NETP=TCP,CLIENT,${serverPort},${serverAddress}`,
    `AT+TCPB=off`,
    `AT+MQTTEN=OFF`,
  ];
}


export default function Dr404ConfiguratorDialog({
  open,
  onClose,
  onComplete,         // ({mac, registerHex, heartbeatHex, ssid, result}) => void
  serial,             // compressor serial — used only as a default suffix in logs
  serverAddress,
  serverPort,
}) {
  const [phase,    setPhase]    = useState("idle");          // idle | ap_sta | reconnect_after_reld | mac | apply | verify | done | error
  const [busy,     setBusy]     = useState(false);
  const [logLines, setLogLines] = useState([]);
  const [mac,      setMac]      = useState("");
  const [registerHex,  setRegisterHex]  = useState("");
  const [heartbeatHex, setHeartbeatHex] = useState("");
  const [ssid,     setSsid]     = useState("");
  const [error,    setError]    = useState("");
  const [confirmReld, setConfirmReld] = useState(false);
  const [confirmZ,    setConfirmZ]    = useState(false);
  // Track which step threw so we can offer "Retry current step" on
  // the error card (preserves MAC + frames, just re-runs the same
  // step). A separate "Retry from the beginning" still hard-resets.
  const [lastFailedRunner, setLastFailedRunner] = useState(null);
  // When the user tries to dismiss while a critical op is running we
  // show an inline confirmation instead of just closing.
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const devRef     = useRef(null);
  const portRef    = useRef(null);    // SerialPort object — kept across reboots
  const abortRef   = useRef(null);
  const supported  = useMemo(() => isWebSerialSupported(), []);

  const log = (line) => setLogLines(L => [...L, { t: Date.now(), s: String(line) }]);

  // ── Cleanup helpers ──────────────────────────────────────────────
  const isCriticalOp = () => busy || devRef.current !== null;

  function abortInFlight() {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* ignore */ }
      abortRef.current = null;
    }
  }

  async function teardownPort() {
    const d = devRef.current;
    devRef.current = null;
    if (d) {
      try { await d.close(); } catch (_) { /* ignore */ }
    }
    // Note: portRef.current is intentionally preserved across
    // teardowns so we can reopen the SAME SerialPort after the
    // gateway reboots — Chrome only honours `requestPort()` while a
    // user gesture is still alive (~5 s), and our 30 s reboot wait
    // blows past that. Re-opening a port we already have permission
    // for needs no fresh gesture.
  }

  // Acquire a SerialPort without prompting if we already have one,
  // falling back to getPorts() (returns previously-authorised ports
  // without a permission prompt), and finally to requestPort()
  // (requires an active user gesture).
  async function acquirePort({ allowRequestPort = true } = {}) {
    if (portRef.current) return portRef.current;
    try {
      if (navigator.serial && typeof navigator.serial.getPorts === "function") {
        const ports = await navigator.serial.getPorts();
        if (ports && ports.length > 0) {
          portRef.current = ports[0];
          log("[serial] reusing previously-authorised port (no prompt)");
          return portRef.current;
        }
      }
    } catch (e) {
      log(`[serial] getPorts() failed: ${e.message || e}`);
    }
    if (!allowRequestPort) {
      throw new Error("no remembered serial port — click Retry to pick one again");
    }
    const port = await Dr404Serial.request();
    portRef.current = port;
    return port;
  }

  function resetWizardState() {
    setPhase("idle");
    setLogLines([]);
    setMac("");
    setRegisterHex("");
    setHeartbeatHex("");
    setSsid("");
    setError("");
    setConfirmReld(false);
    setConfirmZ(false);
    setShowCloseConfirm(false);
    setLastFailedRunner(null);
    portRef.current = null;
  }

  // Triggered by the X icon, Cancel button, etc. Asks for confirmation
  // when there's an in-flight serial operation; otherwise tears
  // everything down cleanly and notifies the parent.
  function requestClose() {
    if (isCriticalOp()) {
      setShowCloseConfirm(true);
      return;
    }
    forceClose();
  }

  function forceClose() {
    abortInFlight();
    // Fire-and-forget so the dialog closes immediately; the port
    // release happens in the background and only logs if it fails.
    teardownPort().catch(() => {});
    setShowCloseConfirm(false);
    onClose();
  }

  // Mount/unmount reset.
  useEffect(() => {
    if (open) {
      resetWizardState();
    } else {
      // Component is being hidden; make sure we always release the
      // port even if the parent skipped our forceClose path.
      abortInFlight();
      teardownPort().catch(() => {});
    }
  }, [open]);

  // Final cleanup on unmount.
  useEffect(() => {
    return () => {
      abortInFlight();
      // Best-effort; useEffect cleanups can't await.
      teardownPort().catch(() => {});
    };
  }, []);

  // ── Step runners ─────────────────────────────────────────────────
  function newAbortController() {
    abortInFlight();
    abortRef.current = new AbortController();
    return abortRef.current.signal;
  }

  // Send an AT command with N attempts and a short backoff. Used by
  // step_read_mac and step_apply so a single transient `+ERR=-1`
  // doesn't force the operator to restart the entire wizard.
  // iter150AZ.121
  async function sendAtWithRetry(dev, cmd, {
                                     attempts = 3, delayMs = 500,
                                     timeoutMs = 10000, signal,
                                   } = {}) {
    let lastErr = null;
    for (let i = 1; i <= attempts; i++) {
      if (signal && signal.aborted) throw new Error("aborted");
      try {
        const result = await dev.sendAt(cmd, { timeoutMs, signal });
        if (i > 1) log(`[retry] ${cmd} succeeded on attempt ${i}/${attempts}`);
        return result;
      } catch (e) {
        if (e.message === "aborted") throw e;
        lastErr = e;
        log(`[retry] ${cmd} attempt ${i}/${attempts}: ${e.message}`);
        if (i < attempts) await sleep(delayMs, signal);
      }
    }
    throw lastErr || new Error(`${cmd} failed after ${attempts} attempts`);
  }

  async function step_connect() {
    setBusy(true); setError("");
    const signal = newAbortController();
    try {
      const port = await acquirePort();   // first call → user gesture prompt
      if (signal.aborted) throw new Error("aborted");
      const dev = await connectAndEnterAtMode(port, log, undefined, { signal });
      devRef.current = dev;
      setPhase("ap_sta");
    } catch (e) {
      if (e.message === "aborted") {
        log("[step] connect aborted by user");
      } else {
        setError(`Connect failed: ${e.message}`);
        setLastFailedRunner(() => step_connect);
        setPhase("error");
      }
    } finally { setBusy(false); }
  }

  async function step_ap_sta_enable() {
    if (!confirmReld) {
      setError("Confirm before sending AT+RELD (will reboot the gateway).");
      return;
    }
    setBusy(true); setError("");
    const signal = newAbortController();
    try {
      const dev = devRef.current;
      if (!dev) throw new Error("serial port is not connected");
      log("[step] enable AP+STA (writes to flash — may take ~10 s)");
      await dev.sendAt("AT+FAPSTA=on", { timeoutMs: 30000, signal });
      log("[step] activation/reset — AT+RELD reboots the gateway");
      try {
        // Device doesn't always ack AT+RELD before rebooting.
        await dev.sendAt("AT+RELD", { timeoutMs: 5000, signal });
      } catch (e) {
        if (e.message === "aborted") throw e;
        log(`[step] AT+RELD response interrupted (expected): ${e.message}`);
      }
      // Don't close the port — keep pinging `+++` until the device
      // comes back, then auto-advance to the MAC step.
      log("[step] polling +++ for gateway to come back online (up to 30 s)...");
      await dev.pollUntilAtMode({ totalMs: 30000, intervalMs: 1500, signal, log });
      setPhase("mac");
    } catch (e) {
      if (e.message === "aborted") {
        log("[step] AP+STA enable aborted by user");
      } else {
        setError(`AP+STA enable failed: ${e.message}`);
        setLastFailedRunner(() => step_ap_sta_enable);
        setPhase("error");
      }
    } finally { setBusy(false); }
  }

  async function step_read_mac() {
    setBusy(true); setError("");
    const signal = newAbortController();
    try {
      const dev = devRef.current;
      if (!dev) throw new Error("serial port is not connected");
      log("[step] reading WiFi MAC");
      // Order: AT+WMAC first because the user confirmed via COMDBG
      // that this firmware variant returns `+ok=<MAC>` for AT+WMAC.
      // Fall back to AT+WSMAC and AT+MAC for older builds. Each
      // command gets 3 attempts via sendAtWithRetry.
      let raw = "";
      const commands = ["AT+WMAC", "AT+WSMAC", "AT+MAC"];
      for (const cmd of commands) {
        if (signal.aborted) throw new Error("aborted");
        try {
          raw = await sendAtWithRetry(dev, cmd, { timeoutMs: 5000, signal });
          if (raw) break;
        } catch (e) {
          if (e.message === "aborted") throw e;
          log(`[step] ${cmd} not supported on this firmware`);
        }
      }
      const macHex = normalizeMac(raw);
      if (!macHex) {
        throw new Error(`could not parse MAC from "${raw}"`);
      }
      setMac(macHex);
      const reg  = buildRegisterFrame(macHex);
      const hb   = buildHeartbeatFrame(macHex);
      const ssidVal = apSsidFromMac(macHex);
      setRegisterHex(reg);
      setHeartbeatHex(hb);
      setSsid(ssidVal);
      log(`[step] MAC ${macHex} → SSID "${ssidVal}"`);
      log(`[step] register frame ${reg}`);
      log(`[step] heartbeat frame ${hb}`);
      setPhase("apply");
    } catch (e) {
      if (e.message === "aborted") {
        log("[step] read MAC aborted by user");
      } else {
        setError(`Read MAC failed: ${e.message}`);
        setLastFailedRunner(() => step_read_mac);
        setPhase("error");
      }
    } finally { setBusy(false); }
  }

  async function step_apply() {
    if (!confirmZ) {
      setError("Confirm before sending the final AT+Z reboot.");
      return;
    }
    setBusy(true); setError("");
    const signal = newAbortController();
    try {
      const dev = devRef.current;
      if (!dev) throw new Error("serial port is not connected");
      const cmds = buildAtSequence({
        macSuffix4: mac.slice(-4),
        registerHex,
        heartbeatHex,
        serverPort,
        serverAddress,
      });
      for (const c of cmds) {
        if (signal.aborted) throw new Error("aborted");
        log(`[step] ${c}`);
        // Each write-to-flash command can take a handful of seconds
        // before the device returns +ok, AND sometimes returns a
        // transient +ERR=-1 (sometimes with line noise) — retry up to
        // 3 times before bailing the whole sequence.
        await sendAtWithRetry(dev, c, { timeoutMs: 10000, signal });
      }
      log("[step] AT+Z (reboot — DR404 takes up to 30 s to come back up)");
      try {
        // Same as RELD: the device may not ack AT+Z before rebooting.
        await dev.sendAt("AT+Z", { timeoutMs: 5000, signal });
      } catch (e) {
        if (e.message === "aborted") throw e;
        log(`[step] AT+Z response interrupted (expected): ${e.message}`);
      }
      // Don't close — poll for the device to come back online, then
      // hand off to verify (which can reuse the same in-AT-mode dev).
      log("[step] polling +++ for gateway to come back online (up to 30 s)...");
      await dev.pollUntilAtMode({ totalMs: 30000, intervalMs: 1500, signal, log });
      setPhase("verify");
    } catch (e) {
      if (e.message === "aborted") {
        log("[step] apply aborted by user");
      } else {
        setError(`Apply failed: ${e.message}`);
        setLastFailedRunner(() => step_apply);
        setPhase("error");
      }
    } finally { setBusy(false); }
  }

  async function step_verify() {
    setBusy(true); setError("");
    const signal = newAbortController();
    try {
      const dev = devRef.current;
      if (!dev) {
        // Should never happen — step_apply polls until the device is
        // back in AT mode before advancing to "verify". Provide a
        // defensive fallback that reopens the cached port anyway.
        log("[step] verify: no live serial wrapper — reopening cached port");
        const port = await acquirePort({ allowRequestPort: false });
        const fresh = new Dr404Serial({ port, log });
        await fresh.open(9600);
        await fresh.enterAtMode({ signal });
        devRef.current = fresh;
      }
      const liveDev = devRef.current;
      log("[step] reading back AT+REGUSR / AT+HEARTDT for verification");
      let okCount = 0;
      try {
        const r = await liveDev.sendAt("AT+REGUSR", { signal });
        if (r && r.toUpperCase().includes(registerHex.toUpperCase())) okCount++;
      } catch (e) { if (e.message === "aborted") throw e; log(`[step] verify register: ${e.message}`); }
      try {
        const r = await liveDev.sendAt("AT+HEARTDT", { signal });
        if (r && r.toUpperCase().includes(heartbeatHex.toUpperCase())) okCount++;
      } catch (e) { if (e.message === "aborted") throw e; log(`[step] verify heartbeat: ${e.message}`); }
      // Release the port now that we're done with it.
      try { await liveDev.close(); } catch (_) { /* ignore */ }
      devRef.current = null;
      log(`[step] verification: ${okCount}/2 fields confirmed`);
      setPhase("done");
    } catch (e) {
      if (e.message === "aborted") {
        log("[step] verify aborted by user");
      } else {
        // Verify is best-effort — if it can't reconnect we still
        // present the generated frames; the operator can decide.
        log(`[step] verify skipped: ${e.message}`);
        setPhase("done");
      }
    } finally { setBusy(false); }
  }

  // Retry from the error card without dismissing the dialog. Aborts
  // any in-flight work, releases the port, and drops back to the
  // idle screen so the operator can pick the serial port again.
  async function step_retry() {
    abortInFlight();
    await teardownPort();
    portRef.current = null;   // force a fresh port-picker on next connect
    setError("");
    setConfirmReld(false);
    setConfirmZ(false);
    setLastFailedRunner(null);
    setPhase("idle");
    log("[retry] full reset — ready to reconnect from scratch");
  }

  // Re-run the step that just failed without resetting any state.
  // Preserves MAC + frames + the active serial port, so most
  // transient `+ERR=-1` blips can be recovered with one click.
  // iter150AZ.121
  async function step_retry_current() {
    const runner = lastFailedRunner;
    if (!runner) return;
    abortInFlight();
    setError("");
    log("[retry] re-running the failed step in place");
    try {
      await runner();
    } catch (e) {
      // The runner already catches its own errors and updates
      // phase/error/lastFailedRunner — this catch is defensive only.
      log(`[retry] uncaught: ${e.message || e}`);
    }
  }

  function commit() {
    // Final guard: regenerate locally and require an exact match before
    // we hand the values off — protects against any race where state
    // accumulators came in out of order.
    const expectReg = buildRegisterFrame(mac);
    const expectHb  = buildHeartbeatFrame(mac);
    if (registerHex !== expectReg || heartbeatHex !== expectHb) {
      setError("Generated frames don't match the MAC — refusing to save.");
      return;
    }
    if (!validateFrame(registerHex,  REGISTER_FN_CODE,  mac).ok ||
        !validateFrame(heartbeatHex, HEARTBEAT_FN_CODE, mac).ok) {
      setError("CRC validation failed — refusing to save.");
      return;
    }
    onComplete({
      mac,
      registerHex,
      heartbeatHex,
      ssid,
      result: {
        applied_at: new Date().toISOString(),
        ap_ssid: ssid,
        server_address: serverAddress,
        server_port: serverPort,
        ap_sta_enabled: true,
      },
    });
    // Reuse forceClose so the port gets torn down on commit too.
    forceClose();
  }

  // ── Render ───────────────────────────────────────────────────────
  return (
    <Dialog open={open}
             onOpenChange={(v) => { if (!v) requestClose(); }}>
      <DialogContent className="sm:max-w-[720px] max-h-[90vh] flex flex-col"
                      data-testid="dr404-configurator"
                      onInteractOutside={(e) => e.preventDefault()}
                      onEscapeKeyDown={(e) => {
                        // Block Esc entirely while a critical op is
                        // running so the operator can't yank the port
                        // out from under an AT sequence by accident.
                        // Otherwise let it bubble up to requestClose.
                        if (isCriticalOp()) e.preventDefault();
                      }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plug size={16} weight="bold" className="text-blue-700" />
            Configure DR404 Gateway · {serial}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-3 min-h-0"
              data-testid="dr404-configurator-scroll">

        {!supported && (
          <div className="bg-amber-50 border border-amber-300 text-amber-800 text-xs p-3 rounded-sm"
                data-testid="dr404-not-supported">
            <Warning size={12} weight="fill" className="inline -mt-0.5 mr-1" />
            Web Serial isn&apos;t available in this browser. Open this page in
            Chrome, Edge, Brave, or Opera, or close this dialog and use the
            Advanced fields to enter frames manually.
          </div>
        )}

        {supported && (
          <>
            <PhaseSummary phase={phase} mac={mac} ssid={ssid}
                           registerHex={registerHex}
                           heartbeatHex={heartbeatHex} />

            {phase === "idle" && (
              <ServerEndpointInfo serverAddress={serverAddress}
                                   serverPort={serverPort}
                                   onConnect={step_connect} busy={busy} />
            )}

            {phase === "ap_sta" && (
              <ConfirmRow label="Confirm: send AT+FAPSTA=on then AT+RELD (reboots gateway)"
                          checked={confirmReld}
                          onChange={setConfirmReld}
                          onProceed={step_ap_sta_enable}
                          actionLabel="Enable AP+STA &amp; reset"
                          busy={busy}
                          testid="dr404-confirm-reld" />
            )}

            {phase === "mac" && (
              <Button data-testid="dr404-read-mac-btn"
                       disabled={busy} onClick={step_read_mac}
                       className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white h-9 px-3 text-sm font-semibold w-fit">
                {busy ? "Reading…" : "Read MAC and generate frames"}
              </Button>
            )}

            {phase === "apply" && (
              <ConfirmRow label="Confirm: write the 14 AT settings then AT+Z (reboots gateway)"
                          checked={confirmZ}
                          onChange={setConfirmZ}
                          onProceed={step_apply}
                          actionLabel="Apply DR404 configuration"
                          busy={busy}
                          testid="dr404-confirm-z" />
            )}

            {phase === "verify" && (
              <Button data-testid="dr404-verify-btn"
                       disabled={busy} onClick={step_verify}
                       className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white h-9 px-3 text-sm font-semibold w-fit">
                {busy ? "Verifying…" : "Read back & verify"}
              </Button>
            )}

            {phase === "done" && (
              <div className="bg-emerald-50 border border-emerald-300 rounded-sm p-3 text-xs"
                    data-testid="dr404-done">
                <div className="flex items-center gap-2 text-emerald-800 font-bold">
                  <CheckCircle size={14} weight="fill" /> Configuration applied
                </div>
                <div className="mt-1 text-emerald-900">
                  Press <b>Save</b> to write these frames into the sequencing group.
                </div>
              </div>
            )}

            {phase === "error" && (
              <div className="bg-red-50 border border-red-300 rounded-sm p-3 text-xs"
                    data-testid="dr404-error">
                <div className="flex items-center gap-2 text-red-800 font-bold">
                  <Warning size={14} weight="fill" /> {error}
                </div>
                <div className="mt-1 text-red-900">
                  Most transient errors recover by re-running the same step.
                  Pick a recovery path:
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {lastFailedRunner && (
                    <Button data-testid="dr404-retry-current-btn"
                             disabled={busy}
                             onClick={step_retry_current}
                             className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white h-8 px-3 text-xs font-semibold">
                      <ArrowsClockwise size={12} weight="bold" className="mr-1" />
                      Retry this step
                    </Button>
                  )}
                  <Button data-testid="dr404-retry-btn"
                           disabled={busy}
                           onClick={step_retry}
                           className="rounded-sm border border-red-300 bg-white text-red-700 hover:bg-red-50 h-8 px-3 text-xs font-semibold">
                    <ArrowsClockwise size={12} weight="bold" className="mr-1" />
                    Retry from the beginning
                  </Button>
                </div>
              </div>
            )}

            <LogPanel logLines={logLines} />
          </>
        )}

        {showCloseConfirm && (
          <CloseConfirm onContinue={() => setShowCloseConfirm(false)}
                         onForceClose={forceClose} />
        )}

        </div>

        <DialogFooter>
          <Button data-testid="dr404-cancel"
                   onClick={requestClose}
                   className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm">
            <X size={12} className="mr-1" /> Cancel
          </Button>
          <Button data-testid="dr404-save"
                   disabled={phase !== "done" || !mac || !registerHex || !heartbeatHex}
                   onClick={commit}
                   className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40 h-9 px-3 text-sm font-semibold">
            <FloppyDisk size={12} weight="bold" className="mr-1" /> Save into group
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PhaseSummary({ phase, mac, ssid, registerHex, heartbeatHex }) {
  const STAGES = [
    ["connect",       "Connect serial"],
    ["ap_sta",        "Enable AP+STA"],
    ["mac",           "Read MAC"],
    ["apply",         "Apply config"],
    ["verify",        "Verify & save"],
  ];
  const reachedFor = (key) => {
    const order = ["idle", "ap_sta", "mac", "apply", "verify", "done"];
    const seen  = order.indexOf(phase);
    const idx   = ["connect", "ap_sta", "mac", "apply", "verify"].indexOf(key);
    return seen >= idx + 1;   // connect is reached once we leave idle (phase=ap_sta)
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3 space-y-2">
      <div className="flex flex-wrap gap-1.5" data-testid="dr404-phase-summary">
        {STAGES.map(([k, label]) => (
          <span key={k}
                 className={"text-[10px] px-2 py-1 rounded-sm uppercase tracking-wider "
                  + (reachedFor(k)
                      ? "bg-blue-100 text-blue-800 font-bold"
                      : "bg-slate-100 text-slate-500")}>
            {label}
          </span>
        ))}
      </div>
      {(mac || registerHex || heartbeatHex) && (
        <div className="text-[11px] font-mono space-y-0.5"
              data-testid="dr404-generated-values">
          {mac          && <div>MAC:        <b>{mac}</b></div>}
          {ssid         && <div>AP SSID:    <b>{ssid}</b></div>}
          {registerHex  && <div>Register:   <b>{registerHex}</b></div>}
          {heartbeatHex && <div>Heartbeat:  <b>{heartbeatHex}</b></div>}
        </div>
      )}
    </div>
  );
}


function ServerEndpointInfo({ serverAddress, serverPort, onConnect, busy }) {
  const ok = !!(serverAddress && serverPort);
  return (
    <div className="space-y-2">
      <div className="text-[11px] text-slate-600">
        The DR404 will be configured to connect to:
      </div>
      <div className="text-[11px] font-mono">
        <b>{serverAddress || "(missing address)"}</b>
        :<b>{serverPort || "(missing port)"}</b>
      </div>
      {!ok && (
        <div className="bg-amber-50 border border-amber-300 text-amber-800 text-xs p-2 rounded-sm"
              data-testid="dr404-missing-endpoint">
          Set TCP server address & port in the row before opening this dialog.
        </div>
      )}
      <Button data-testid="dr404-connect-btn"
              disabled={busy || !ok} onClick={onConnect}
              className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40 h-9 px-3 text-sm font-semibold w-fit">
        <Plug size={12} weight="bold" className="mr-1" />
        {busy ? "Connecting…" : "Connect serial"}
      </Button>
    </div>
  );
}


function ConfirmRow({ label, checked, onChange, onProceed, actionLabel, busy, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <label className="flex items-start gap-2 text-xs text-slate-700">
        <input type="checkbox" className="mt-0.5"
                checked={checked}
                onChange={e => onChange(e.target.checked)}
                data-testid={`${testid}-checkbox`} />
        <span dangerouslySetInnerHTML={{ __html: label }} />
      </label>
      <Button data-testid={`${testid}-go`}
               disabled={!checked || busy} onClick={onProceed}
               className="rounded-sm bg-red-600 hover:bg-red-700 text-white disabled:opacity-40 h-9 px-3 text-sm font-semibold w-fit">
        {busy ? "…" : actionLabel}
      </Button>
    </div>
  );
}


// Inline confirmation banner shown when the user tries to close the
// dialog while a serial operation is still active. Forces an explicit
// choice instead of accidentally bricking the AT sequence.
function CloseConfirm({ onContinue, onForceClose }) {
  return (
    <div className="bg-amber-50 border border-amber-400 rounded-sm p-3 space-y-2"
          data-testid="dr404-close-confirm">
      <div className="flex items-center gap-2 text-amber-900 font-bold text-sm">
        <Warning size={14} weight="fill" /> Cancel the configuration?
      </div>
      <div className="text-[11px] text-amber-900">
        A serial operation is in progress. Closing now will abort the
        current AT sequence, release the serial port, and discard any
        generated frames that have not been saved yet.
      </div>
      <div className="flex gap-2">
        <Button data-testid="dr404-close-confirm-stay"
                 onClick={onContinue}
                 className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-8 px-3 text-xs">
          Keep configuring
        </Button>
        <Button data-testid="dr404-close-confirm-force"
                 onClick={onForceClose}
                 className="rounded-sm bg-red-600 hover:bg-red-700 text-white h-8 px-3 text-xs font-semibold">
          <X size={12} className="mr-1" /> Force cancel
        </Button>
      </div>
    </div>
  );
}


function LogPanel({ logLines }) {
  // iter150AZ.112 — keep the most recent entry visible without making
  // the operator scroll. We use a ref + scrollIntoView in a layout
  // effect so the panel stays "live" while AT commands run.
  const endRef = useRef(null);
  useEffect(() => {
    if (endRef.current && typeof endRef.current.scrollIntoView === "function") {
      endRef.current.scrollIntoView({ block: "end", behavior: "instant" });
    }
  }, [logLines]);
  return (
    <div className="bg-slate-900 text-emerald-100 font-mono text-[10px] p-2 rounded-sm
                     max-h-[180px] overflow-y-auto"
          data-testid="dr404-log">
      {logLines.length === 0 && (
        <div className="text-slate-500">[ log will appear here ]</div>
      )}
      {logLines.map((l, i) => (
        <div key={i}>
          <span className="text-slate-500">[{new Date(l.t).toLocaleTimeString()}]</span>
          {" "}{l.s}
        </div>
      ))}
      <div ref={endRef} />
    </div>
  );
}
